import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Check, X, Ban } from 'lucide-react';
import { Badge } from '@medikei/ui';
import { Button } from '@/components/ui/Button';


import { Pagination, PaginationInfo } from '@/components/ui/pagination';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/EmptyState';
import type { Partner, PartnerStatus, KYCStatus, PartnerLevel } from '../types/admin-partner';

interface PartnerTableProps {
  partners: Partner[];
  isLoading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onSuspend: (id: string) => void;
  onActivate?: (id: string) => void;
  isApproving?: boolean;
  isRejecting?: boolean;
  isSuspending?: boolean;
  isActivating?: boolean;
}

const statusVariants: Record<PartnerStatus, 'warning' | 'success' | 'danger' | 'outline'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  ACTIVE: 'success',
  REJECTED: 'danger',
  SUSPENDED: 'outline',
};

const statusLabels: Record<PartnerStatus, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  ACTIVE: 'Ativo',
  REJECTED: 'Rejeitado',
  SUSPENDED: 'Suspenso',
};

const kycVariants: Record<KYCStatus, 'warning' | 'success' | 'danger' | 'outline'> = {
  PENDING: 'warning',
  SUBMITTED: 'warning',
  VERIFIED: 'success',
  APPROVED: 'success',
  REJECTED: 'danger',
  NOT_SUBMITTED: 'outline',
};

const kycLabels: Record<KYCStatus, string> = {
  PENDING: 'Pendente',
  SUBMITTED: 'Enviado',
  VERIFIED: 'Verificado',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  NOT_SUBMITTED: 'Não Enviado',
};

const levelLabels: Record<PartnerLevel, string> = {
  BRONZE: 'Bronze',
  SILVER: 'Prata',
  GOLD: 'Ouro',
  PLATINUM: 'Platina',
};

export function PartnerTable({
  partners,
  isLoading,
  total,
  page,
  pageSize,
  onPageChange,
  onApprove,
  onReject,
  onSuspend,
  onActivate,
  isApproving,
  isRejecting,
  isSuspending,
  isActivating,
}: PartnerTableProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === partners.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(partners.map((p) => p.id)));
    }
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const formatCpfCnpj = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (digits.length === 14) {
      return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  };

  if (isLoading) {
    return <LoadingOverlay message="Carregando parceiros..." />;
  }

  if (partners.length === 0) {
    return (
      <EmptyState
        title="Nenhum parceiro encontrado"
        description="Não há parceiros cadastrados com os filtros selecionados."
      />
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedIds.size === partners.length && partners.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-input"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Nome</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">CPF/CNPJ</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">KYC</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Nível</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Data</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {partners.map((partner) => (
              <tr key={partner.id} className="hover:bg-muted/30">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(partner.id)}
                    onChange={() => toggleSelect(partner.id)}
                    className="rounded border-input"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-xs font-medium text-primary">
                      {partner.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{partner.name}</p>
                      <p className="text-xs text-muted-foreground">{partner.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">{formatCpfCnpj(partner.cpfCnpj)}</td>
                <td className="px-4 py-3">
                  <Badge variant={statusVariants[partner.status]}>
                    {statusLabels[partner.status]}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={kycVariants[partner.kycStatus]}>
                    {kycLabels[partner.kycStatus]}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm">{levelLabels[partner.level]}</span>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {formatDate(partner.createdAt)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <Link to={`/admin/partners/${partner.id}`}>
                      <Button variant="ghost" size="sm" title="Visualizar">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </Link>
                    {partner.status === 'PENDING' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onApprove(partner.id)}
                          disabled={isApproving}
                          title="Aprovar"
                          className="text-success hover:text-success"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onReject(partner.id)}
                          disabled={isRejecting}
                          title="Rejeitar"
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {(partner.status === 'APPROVED' || partner.status === 'ACTIVE') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSuspend(partner.id)}
                        disabled={isSuspending}
                        title="Suspender"
                        className="text-warning hover:text-warning"
                      >
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                    {partner.status === 'SUSPENDED' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => (onActivate ? onActivate(partner.id) : onApprove(partner.id))}
                        disabled={isActivating || isApproving}
                        title="Ativar"
                        className="text-success hover:text-success"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <PaginationInfo
          currentPage={page}
          pageSize={pageSize}
          totalItems={total}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
